import { useEffect, useLayoutEffect, useRef, useState, useId } from "react";

export function dateToDisplay(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "";
}

export function displayToDate(value) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!m) return "";
  const [, day, month, year] = m;
  const y = Number(year), mo = Number(month), d = Number(day);
  const leap = y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return y >= 1 && mo >= 1 && mo <= 12 && d >= 1 && d <= days[mo - 1] ? `${year}-${month}-${day}` : "";
}

export function formatDateDigits(value) {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.slice(0, 2) + (digits.length >= 2 ? '/' : '') + digits.slice(2, 4) + (digits.length >= 4 ? '/' : '') + digits.slice(4);
}

export default function DateInput({ value, onChange, min, max, ...props }) {
  const [text, setText] = useState(() => dateToDisplay(value));
  const [touched, setTouched] = useState(false);
  const focused = useRef(false);
  const input = useRef(null);
  const cursor = useRef(null);
  const errorId = useId();
  useEffect(() => { if (!focused.current) setText(dateToDisplay(value)); }, [value]);
  useLayoutEffect(() => { if (cursor.current !== null && input.current === document.activeElement) input.current.setSelectionRange(cursor.current.start, cursor.current.end); cursor.current = null; }, [text]);
  const update = (iso) => onChange({ target: { value: iso } });
  const typed = (e) => {
    const raw = e.target.value;
    const before = raw.slice(0, e.target.selectionStart ?? raw.length).replace(/\D/g, '').slice(0, 8).length;
    const next = formatDateDigits(raw);
    const pos = Math.min(next.length, before + (before >= 2 ? 1 : 0) + (before >= 4 ? 1 : 0));
    cursor.current = { start: pos, end: pos };
    if (next === text) { e.target.value = next; e.target.setSelectionRange(pos, pos); cursor.current = null; }
    setText(next);
    const iso = displayToDate(next);
    update(iso && (!min || iso >= min) && (!max || iso <= max) ? iso : "");
  };
  const date = displayToDate(text);
  const invalid = text && (!date || (min && date < min) || (max && date > max));
  const showError = invalid && (touched || text.replace(/\D/g, '').length === 8);
  const keyDown = (e) => {
    const el = e.currentTarget, start = el.selectionStart, end = el.selectionEnd;
    // Replacing a selected segment must not shift the following month or year.
    if (!e.ctrlKey && !e.metaKey && !e.altKey && /^\d$/.test(e.key) && end - start > 1 && /^\d+$/.test(text.slice(start, end))) {
      e.preventDefault();
      const next = text.slice(0, start) + e.key + text.slice(start + 1);
      el.value = next;
      el.setSelectionRange(start + 1, start + 1);
      typed({ target: el });
      if (next === text) el.setSelectionRange(start + 1, end);
      else cursor.current = { start: start + 1, end };
      return;
    }
    if (start !== end) return;
    if (e.key === 'Backspace' && start > 0 && text[start - 1] === '/') el.setSelectionRange(start - 2, start);
    if (e.key === 'Delete' && text[start] === '/') el.setSelectionRange(start, Math.min(text.length, start + 2));
  };
  return <span className="rr-date-input">
    <span className="rr-date-control">
    <input {...props} ref={input} type="text" inputMode="numeric" placeholder="dd/mm/yyyy" autoComplete="off" value={text} aria-invalid={!!showError} aria-describedby={showError ? errorId : undefined} onFocus={() => { focused.current = true; }} onBlur={() => { focused.current = false; setTouched(true); }} onKeyDown={keyDown} onChange={typed} />
    <span className="rr-date-icon" aria-hidden="true">▦</span>
    <input type="date" className="rr-date-picker" aria-label={`Choose ${props['aria-label'] || 'date'} from calendar`} value={value || ""} min={min} max={max} onChange={(e) => { setText(dateToDisplay(e.target.value)); update(e.target.value); }} />
    </span>
    {showError && <span id={errorId} className="rr-date-error" role="status">{date ? 'Date is outside the allowed range.' : 'Enter a valid date in DD/MM/YYYY format.'}</span>}
  </span>;
}
